/**
 * LiveReadinessScore — Compact inline readiness score widget
 *
 * Shows during the assessment flow, updating in real-time as the borrower
 * answers questions. Includes gamification: level badges, encouraging
 * micro-copy, and smooth animated transitions.
 */
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Sprout, Flame, Zap, ShieldCheck, Trophy, Star, ArrowUp, Radar } from 'lucide-react';

// ── Score tiers with gamification labels ────────────────────────
const TIERS = [
  { min: 0,  label: 'Getting Started', color: 'var(--lk-gray-400)',    Icon: Sprout,      bg: 'var(--lk-bg-surface-2)' },
  { min: 15, label: 'On Your Way',     color: 'var(--lk-warning-500)', Icon: Flame,        bg: 'var(--lk-warning-50)' },
  { min: 30, label: 'Building Momentum',color: 'var(--lk-warning-400)',Icon: Zap,          bg: 'var(--lk-warning-50)' },
  { min: 50, label: 'Strong Profile',   color: 'var(--lk-primary-500)',Icon: ShieldCheck,  bg: 'var(--lk-primary-50)' },
  { min: 70, label: 'Excellent',        color: 'var(--lk-success-500)',Icon: Trophy,       bg: 'var(--lk-success-50)' },
  { min: 90, label: 'Outstanding',      color: 'var(--lk-success-500)',Icon: Star,         bg: 'var(--lk-success-50)' },
];

const getTier = (score) => {
  for (let i = TIERS.length - 1; i >= 0; i--) {
    if (score >= TIERS[i].min) return TIERS[i];
  }
  return TIERS[0];
};

// ── Encouraging micro-copy based on score delta ─────────────────
const ENCOURAGEMENTS = [
  'Nice! Your readiness just went up.',
  'Great answer — score improved!',
  'You\'re making progress!',
  'Keep going — looking strong!',
  'Every answer helps. You\'re doing great.',
];

const LiveReadinessScore = ({ compact = false }) => {
  const readinessScore = useSelector((state) => state.loanApplication.readinessScore);
  const [prevScore, setPrevScore] = useState(readinessScore);
  const [showBump, setShowBump] = useState(false);
  const [bumpText, setBumpText] = useState('');
  const [levelUp, setLevelUp] = useState(false);
  const timerRef = useRef(null);

  const tier = getTier(readinessScore);
  const prevTier = getTier(prevScore);

  useEffect(() => {
    if (readinessScore > prevScore) {
      // Score went up — show encouragement
      const msg = ENCOURAGEMENTS[Math.floor(Math.random() * ENCOURAGEMENTS.length)];
      setBumpText(msg);
      setShowBump(true);

      // Check for level-up
      if (tier.label !== prevTier.label) {
        setLevelUp(true);
      }

      // Clear after animation
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setShowBump(false);
        setLevelUp(false);
      }, 2500);
    }
    setPrevScore(readinessScore);

    return () => clearTimeout(timerRef.current);
  }, [readinessScore]);

  const TierIcon = tier.Icon;

  return (
    <div style={{
      backgroundColor: 'var(--lk-bg-surface-1)',
      borderRadius: 'var(--lk-radius-xl)',
      border: '1px solid var(--lk-border)',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
    }}>
      {/* Header */}
      <div style={{
        padding: 'var(--lk-space-4) var(--lk-space-5) var(--lk-space-3)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
          <Radar size={18} color="var(--lk-primary-500)" />
          <span style={{ fontSize: 'var(--lk-text-xs)', fontWeight: 600, color: 'var(--lk-text-secondary)' }}>
            Mortgage Readiness
          </span>
        </div>
        {/* Tier badge */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 10px', borderRadius: 12,
          backgroundColor: tier.bg, fontSize: 11, fontWeight: 600, color: tier.color,
          transition: 'all 0.4s ease',
          transform: levelUp ? 'scale(1.15)' : 'scale(1)',
        }}>
          <TierIcon size={13} />
          {tier.label}
        </div>
      </div>

      {/* Score + Progress */}
      <div style={{ padding: '0 var(--lk-space-5) var(--lk-space-4)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-2)' }}>
          <span style={{
            fontSize: 32, fontWeight: 700, lineHeight: 1,
            color: tier.color, transition: 'color 0.4s ease',
          }}>
            {readinessScore}
          </span>
          <span style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)', marginBottom: 4 }}>/100</span>
        </div>

        <div style={{ height: 6, borderRadius: 3, backgroundColor: 'var(--lk-gray-100)', overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${readinessScore}%`, borderRadius: 3,
            backgroundColor: tier.color,
            transition: 'width 0.6s ease, background-color 0.4s ease',
          }} />
        </div>

        {/* Encouragement toast */}
        <div style={{
          overflow: 'hidden',
          maxHeight: showBump ? 40 : 0, opacity: showBump ? 1 : 0,
          transition: 'all 0.4s ease',
          marginTop: showBump ? 10 : 0,
        }}>
          <div style={{
            fontSize: 12, color: tier.color, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <ArrowUp size={14} />
            {bumpText}
          </div>
        </div>

        {/* Level-up flash */}
        {levelUp && (
          <div style={{
            marginTop: 8, padding: '6px 12px', borderRadius: 'var(--lk-radius-md)',
            backgroundColor: tier.bg, fontSize: 12, fontWeight: 600,
            color: tier.color, textAlign: 'center',
            animation: 'fadeInUp 0.4s ease',
          }}>
            <TierIcon size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
            Level Up! You reached "{tier.label}"
          </div>
        )}
      </div>

      {!compact && (
        <div style={{
          padding: '10px var(--lk-space-5)',
          borderTop: '1px solid var(--lk-border-subtle)',
          fontSize: 11, color: 'var(--lk-text-quaternary)',
        }}>
          Score improves as you answer more questions
        </div>
      )}
    </div>
  );
};

export default LiveReadinessScore;
